import React from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { macGoalAppearance } from '@/components/Coworker/workGoalAppearance';

// In normal layout, outside the clipped input bubble: native hit testing cannot
// reach an absolutely positioned popup extending beyond its parent's bounds.
export default function GoalCommandSuggestions({ prompt, onSelect, onDismiss }) {
  const { colors, theme } = useTheme();
  const isMac = Platform.OS === 'macos';
  const palette = isMac ? macGoalAppearance(theme) : null;
  if (!prompt) return null;
  return (
    <View testID="goal-command-popup" style={[styles.container, isMac && { marginHorizontal: 0, marginBottom: 10 }, {
      backgroundColor: palette?.surface || colors.solid_color_primary,
      borderColor: palette?.border || colors.border_color_secondary,
    }]}>
      <ScrollView keyboardShouldPersistTaps="always" scrollEnabled={false}>
        <View style={styles.row}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Set a long-running goal"
            onPress={onSelect}
            {...(Platform.OS === 'web' ? { onMouseDown: event => event.preventDefault() } : {})}
            style={({ hovered, pressed }) => [styles.command, isMac && styles.macCommand,
              (pressed || (isMac && hovered)) && { backgroundColor: palette?.hover || colors.solid_color_secondary }]}
          >
            {isMac && <View style={[styles.symbol, { backgroundColor: palette.field }]}>
              <Icon name="bullseye" size={16} color={palette.text} />
            </View>}
            <View style={[styles.copy, isMac && { flex: 1 }]}>
              <Text size="s" weight="medium" color="primary" style={isMac && { fontFamily: 'System', fontSize: 13, color: palette.text }}>
                {isMac ? 'Set a goal' : '/goal · Set a long-running goal'}
              </Text>
              <Text size="xs" color="secondary" style={isMac && { fontFamily: 'System', fontSize: 12, color: palette.secondary }}>
                Set an objective for Work to keep pursuing.
              </Text>
            </View>
            {isMac && <Text size="xs" style={{ fontFamily: 'System', fontSize: 11, color: palette.secondary }}>/goal</Text>}
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Dismiss goal suggestion"
            onPress={onDismiss}
            style={styles.dismiss}
          >
            <Text size="n" color="secondary" style={isMac && { color: palette.secondary }}>×</Text>
          </Pressable>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { marginHorizontal: 8, marginBottom: 6, borderRadius: 12, borderWidth: 1, overflow: 'hidden', flexShrink: 0 },
  row: { flexDirection: 'row', alignItems: 'stretch' },
  command: { flex: 1, paddingHorizontal: 12, paddingVertical: 12, gap: 4 },
  macCommand: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  symbol: { width: 32, height: 32, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  copy: { gap: 4 },
  dismiss: { width: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
});
